//eslint:disable max-func-body-length
import * as React from 'react';
import { OptionsProps } from 'app/components/options-handler';
import { CodetableMetadata } from 'app/area/codetable/types-store';
import { CodeComplexValue } from 'app/area/shared/codetable/types';
import { Translations } from 'app/area/codetable/item/translation/translations';
import { ValidationProps } from 'ui/common/validation/types';
import { Details } from 'app/area/codetable/item/details/details';
import { Visibility } from 'app/area/codetable/item/visibility/visibility';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Component, RefObject } from 'react';
import { CodeEditSectionType, CodeActionType, ScreenSlots, Slot, ReadOnlyType, ReadOnlyConfiguration } from 'app/area/codetable/item/types';
import styles from '../../screen-template.scss';
import { withMetroLine } from 'app/components/metro-line/with-metro-line';
import { WithMetroLineProps, WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { CodeCodetableTypes } from 'app/area/codetable/item/code-pickers/code-type';
import { shouldRenderTypes } from 'app/area/codetable/item/codetable-item-form-render-helpers';
import { CodeItem } from 'app/area/codetable/item/types-store';
import { CodetableTranslation } from 'app/area/codetable/constants';
import { CodetableOptions } from 'app/area/codetable/common';
import { actionCreators } from 'app/area/codetable/item/actions';
import { RiskMatrixConfiguration } from 'app/area/codetable/item/screen-renderer/screen-sections/risk-matrix-reminder/risk-matrix-configuration';

export interface CodeScreenTemplateExternalProps extends WithMetroLineProps<CodeEditSectionType> {
  screenPayload: ScreenSlots;
  codetableId: number;
  section: string;
  actionType: CodeActionType;
  codeItem: CodeItem;
  originalCodeItem: CodeItem;
  metadata: CodetableMetadata;
  language: CodeComplexValue<number>;
  locale: string;
  translation: CodetableTranslation;
  hasTranslationsToggled: boolean;
  codetableOptions: { [key: string]: CodetableOptions };
  validation: { [key: string]: ValidationProps };
  sectionRef?: RefObject<HTMLDivElement>;
  hasBigStickyHeader?: boolean;
  readOnly?: boolean;
  readOnlyConfig?: ReadOnlyConfiguration;
  breadcrumbs: number[];
  screenProps?: { [key: string]: any };
}

export type CodeScreenTemplateProps = CodeScreenTemplateExternalProps & OptionsProps
  & Partial<typeof actionCreators> & WithMetroLineProps<CodeEditSectionType> & WithMetroLineWrappedComponentProps<CodeEditSectionType>;

class ScreenTemplatePure extends Component<CodeScreenTemplateProps> {

  private getSectionSlot = (section: CodeEditSectionType): Slot =>
    this.props.screenPayload ? this.props.screenPayload[section] : undefined;

  public render() {
    const {
      codeItem, breadcrumbs, metadata, language, switchTranslationsToggle, getRef, hasBigStickyHeader, codetableOptions, readOnlyConfig,
      copyDescriptionToAllTranslations, copyLongDescriptionToAllTranslations, loadOptions, translation, actionType,
      updateDescription, updateLongDescription, loadCodetableOptions,
      updateCodetableTypes, validation, locale, screenProps
    } = this.props;

    const renderTranslationsOnTop = actionType === CodeActionType.Duplicate || shouldRenderTranslationsOnTop(codeItem);
    const shouldRenderTypesSection = shouldRenderTypes(metadata);

    return <div className={styles.screenTemplate}>
      <div>
        {renderTranslationsOnTop && <MetroLineSection<CodeEditSectionType>
          {...this.props}
          sectionType={CodeEditSectionType.Translations}
          hasBigStickyHeader={hasBigStickyHeader}
        >
          <Translations
            sectionRef={getRef(CodeEditSectionType.Translations)}
            sectionSlots={this.getSectionSlot(CodeEditSectionType.Translations)}
            {...this.props}
            metadata={metadata}
            mainLanguage={language}
            copyDescriptionToAllTranslations={copyDescriptionToAllTranslations}
            copyLongDescriptionToAllTranslations={copyLongDescriptionToAllTranslations}
            switchTranslationsToggle={switchTranslationsToggle}
            onDescriptionChange={updateDescription}
            onLongDescriptionChange={updateLongDescription}
          />
        </MetroLineSection>
        }
        <MetroLineSection<CodeEditSectionType>
          {...this.props}
          sectionType={CodeEditSectionType.RiskMatrixReminderSection}
          hasBigStickyHeader={hasBigStickyHeader}
        >
          <RiskMatrixConfiguration
            sectionRef={getRef(CodeEditSectionType.RiskMatrixReminderSection)}
            sectionSlots={this.getSectionSlot(CodeEditSectionType.RiskMatrixReminderSection)}
            rootId={breadcrumbs?.[0]}
            addInfoText={screenProps?.addInfoText}
            editInfoText={screenProps?.editInfoText}
            {...this.props}
          />
        </MetroLineSection>
        <MetroLineSection<CodeEditSectionType>
          {...this.props}
          sectionType={CodeEditSectionType.Details}
          hasBigStickyHeader={hasBigStickyHeader}
        >
          <Details
            sectionRef={getRef(CodeEditSectionType.Details)}
            sectionSlots={this.getSectionSlot(CodeEditSectionType.Details)}
            {...this.props}
          />
        </MetroLineSection>

        <MetroLineSection<CodeEditSectionType>
          {...this.props}
          sectionType={CodeEditSectionType.Visibility}
          hasBigStickyHeader={hasBigStickyHeader}
        >
          <Visibility
            sectionRef={getRef(CodeEditSectionType.Visibility)}
            sectionSlots={this.getSectionSlot(CodeEditSectionType.Visibility)}
            {...this.props}
          />

        </MetroLineSection>
        {shouldRenderTypesSection && <MetroLineSection<CodeEditSectionType>
          {...this.props}
          sectionType={CodeEditSectionType.Types}
          hasBigStickyHeader={hasBigStickyHeader}
        >
          <CodeCodetableTypes
            sectionRef={getRef(CodeEditSectionType.Types)}
            sectionSlots={this.getSectionSlot(CodeEditSectionType.Types)}
            readOnly={readOnlyConfig?.[ReadOnlyType.all]}
            metadata={metadata}
            codeItem={codeItem}
            breadcrumbs={breadcrumbs}
            options={codetableOptions}
            loadOptions={loadOptions}
            loadCodetableOptions={loadCodetableOptions}
            onChange={updateCodetableTypes}
            displaySelectAll={true}
            translation={translation}
            validation={validation}
            locale={locale} />
        </MetroLineSection>
        }
        {!renderTranslationsOnTop && <MetroLineSection<CodeEditSectionType>
          {...this.props}
          sectionType={CodeEditSectionType.Translations}
          hasBigStickyHeader={hasBigStickyHeader}
        >
          <Translations
            sectionRef={getRef(CodeEditSectionType.Translations)}
            sectionSlots={this.getSectionSlot(CodeEditSectionType.Translations)}
            {...this.props}
            metadata={metadata}
            mainLanguage={language}
            copyDescriptionToAllTranslations={copyDescriptionToAllTranslations}
            copyLongDescriptionToAllTranslations={copyLongDescriptionToAllTranslations}
            switchTranslationsToggle={switchTranslationsToggle}
            onDescriptionChange={updateDescription}
            onLongDescriptionChange={updateLongDescription}
          />
        </MetroLineSection>
        }
      </div>
    </div >;
  }
}

export const SeverityScreen = withMetroLine<CodeScreenTemplateExternalProps, CodeEditSectionType>()(ScreenTemplatePure);

const shouldRenderTranslationsOnTop = (codeItem: CodeItem) => codeItem && !isDefinedIncludingZero(codeItem.id);
